// Interactive wizard component for Instagram integration prerequisites
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, CheckCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';

interface PrerequisiteStep {
  id: string;
  question: string;
  description?: string;
  instructions?: React.ReactNode;
}

export function InstagramPrerequisiteWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  
  const steps: PrerequisiteStep[] = [
    {
      id: 'business_account',
      question: 'Is your Instagram account already a Business Account?',
      description: 'Instagram Business accounts have access to analytics and can be connected to Facebook pages.',
      instructions: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">How to switch to a Business Account</h3>
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <strong>Use Your Phone!</strong> These steps cannot be done on desktop in a browser.
            </AlertDescription>
          </Alert>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">1</Badge>
              <span>Open the Instagram app</span>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">2</Badge>
              <span>Go to your Profile</span>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">3</Badge>
              <span>Tap the menu (≡) → Account type and tools</span>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">4</Badge>
              <div>
                <span>Tap <strong>Switch to professional account</strong></span>
                <Alert className="mt-2 bg-red-50 border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    You must select <strong>"Business"</strong> NOT "Creator" (creator accounts do not get prompted to connect to a Facebook page)
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'facebook_page',
      question: 'Do you have a Facebook Page for your business?',
      description: 'A Facebook Page is required to connect Instagram Business accounts.',
      instructions: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">How to create a Facebook Page</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">1</Badge>
              <span>Go to <a href="https://www.facebook.com/business/tools/facebook-pages" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">facebook.com/business/tools/facebook-pages</a></span>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">2</Badge>
              <span>Fill out the form as best you can</span>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">3</Badge>
              <span>Click 'Create Page'</span>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">4</Badge>
              <span>Fill out more information if you want (optional)</span>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">5</Badge>
              <span>Continue through the wizard process until done</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">Your page is now ready to connect!</p>
        </div>
      )
    },
    {
      id: 'instagram_facebook_connected',
      question: 'Is your Instagram Business Account connected to your Facebook Page?',
      description: 'This connection verifies ownership and enables scheduling features.',
      instructions: (
        <div className="space-y-4">
          <h3 className="font-semibold text-lg">How to verify ownership with Facebook</h3>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">1</Badge>
              <span>Go to <a href="https://business.facebook.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">business.facebook.com</a></span>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">2</Badge>
              <span>Log in</span>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">3</Badge>
              <span>Click Settings</span>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">4</Badge>
              <span>Click 'Add Instagram'</span>
            </div>
            <div className="flex items-start gap-3">
              <Badge className="bg-blue-600 mt-0.5">5</Badge>
              <span>Login to Instagram with your Instagram Business account</span>
            </div>
          </div>
          
          <Alert className="mt-4">
            <AlertDescription>
              You'll be redirected to a random page, but it did work! To verify the connection:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Go back to <a href="https://business.facebook.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">business.facebook.com</a></li>
                <li>Click "Settings" in the left nav menu</li>
                <li>Click "Add Instagram"</li>
                <li>There should be a green checkmark next to "Add an Instagram Profile"</li>
              </ul>
            </AlertDescription>
          </Alert>
          
          <p className="text-sm text-muted-foreground">
            Note: The Facebook UI might continue to prompt you to add a Facebook page for a while. That's ok - Facebook needs to up their game here! 😅
          </p>
        </div>
      )
    }
  ];

  const handleAnswer = (value: string) => {
    setAnswers({
      ...answers,
      [steps[currentStep].id]: value === 'yes'
    });
  };

  const isCurrentStepAnswered = () => {
    return answers[steps[currentStep].id] !== undefined;
  };

  const getProgress = () => {
    const answeredSteps = Object.keys(answers).length;
    return (answeredSteps / steps.length) * 100;
  };

  const getUncompleteSteps = () => {
    return steps.filter(step => answers[step.id] === false);
  };

  const isComplete = () => {
    return Object.keys(answers).length === steps.length && 
           Object.values(answers).every(answer => answer === true);
  };

  const currentStepData = steps[currentStep];
  const currentAnswer = answers[currentStepData.id];

  // Summary view when all questions are answered
  if (Object.keys(answers).length === steps.length) {
    const uncompleteSteps = getUncompleteSteps();
    
    return (
      <div className="space-y-6">
        <Progress value={100} className="h-2" />
        
        {isComplete() ? (
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-semibold text-green-800">All prerequisites completed!</h3>
              </div>
              <p className="text-green-700">
                You're ready to connect your Instagram Business Account to Rainfactory. 
                Continue with the integration steps below.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Alert className="bg-amber-50 border-amber-200">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Setup Required:</strong> You need to complete {uncompleteSteps.length} prerequisite{uncompleteSteps.length > 1 ? 's' : ''} before connecting to Rainfactory.
              </AlertDescription>
            </Alert>
            
            <div className="space-y-4">
              <h3 className="font-semibold">Complete these steps:</h3>
              {uncompleteSteps.map((step, index) => (
                <Card key={step.id}>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3 mb-4">
                      <Badge variant="outline" className="mt-0.5">{index + 1}</Badge>
                      <div className="flex-1">
                        <h4 className="font-medium mb-2">{step.question.replace('?', '')}</h4>
                        {step.instructions}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div className="flex justify-between">
              <Button
                variant="outline"
                onClick={() => {
                  setAnswers({});
                  setCurrentStep(0);
                }}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Start Over
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Progress value={getProgress()} className="h-2" />
      
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">{currentStepData.question}</h3>
              {currentStepData.description && (
                <p className="text-muted-foreground text-sm">{currentStepData.description}</p>
              )}
            </div>
            
            <RadioGroup 
              value={currentAnswer === undefined ? '' : currentAnswer ? 'yes' : 'no'}
              onValueChange={handleAnswer}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="yes" />
                <Label htmlFor="yes">Yes, I have this set up</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="no" />
                <Label htmlFor="no">No, I need to set this up</Label>
              </div>
            </RadioGroup>
            
            {currentAnswer === false && currentStepData.instructions && (
              <>
                <Separator className="my-4" />
                {currentStepData.instructions}
              </>
            )}
          </div>
        </CardContent>
      </Card>
      
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentStep(currentStep - 1)}
          disabled={currentStep === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Previous
        </Button>
        
        <span className="text-sm text-muted-foreground self-center">
          Step {currentStep + 1} of {steps.length}
        </span>
        
        <Button
          onClick={() => setCurrentStep(currentStep + 1)}
          disabled={!isCurrentStepAnswered() || currentStep === steps.length - 1}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}